import { ReactNode, useCallback, useEffect, useState } from "react";
import { AxiosError, AxiosResponse } from "axios";

import {
  SubscriptionContext,
  SubscriptionContextType,
} from "@/contexts/subscriptionContext";
import { subscriptionService } from "@/services/subscription";
import { Subscription } from "@/types/subscription";

interface SubscriptionContextProviderProps {
  isAuthenticated: boolean;
  children: ReactNode;
}

export const SubscriptionContextProvider = ({
  isAuthenticated,
  children,
}: SubscriptionContextProviderProps) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [hasEverSubscribed, setHasEverSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLink, setPortalLink] = useState("");
  const [isPortalLinkLoading, setIsPortalLinkLoading] = useState(false);
  const [portalLinkError, setPortalLinkError] = useState<string | null>(null);

  const fetchSubscriptions = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscriptions([]);
      setHasEverSubscribed(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await subscriptionService.fetchAllSubscriptions();
      setSubscriptions(response?.data?.subscriptions || []);
      setHasEverSubscribed(response?.data?.has_ever_subscribed || false);
    } catch (error: any) {
      setError(error?.message || "Failed to fetch subscriptions");
      console.error("Error fetching subscriptions:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const refetchSubscriptions = useCallback(async () => {
    await fetchSubscriptions();
  }, [fetchSubscriptions]);

  const fetchPortalLink = useCallback(() => {
    if (
      !isAuthenticated ||
      (subscriptions.length === 0 && !hasEverSubscribed)
    ) {
      setPortalLink("");
      setIsPortalLinkLoading(false);
      return;
    }

    setIsPortalLinkLoading(true);
    setPortalLinkError(null);

    subscriptionService.fetchPortalLink(
      (response: AxiosResponse) => {
        const { url } = response.data;
        setPortalLink(url);
        setIsPortalLinkLoading(false);
      },
      (error: AxiosError) => {
        console.error("Error fetching portal link:", error);
        setPortalLinkError(error?.message || "Failed to fetch portal link");
        setIsPortalLinkLoading(false);
      }
    );
  }, [isAuthenticated, subscriptions.length, hasEverSubscribed]);

  useEffect(() => {
    fetchPortalLink();
  }, [fetchPortalLink]);

  const findSubscriptionBySlug = useCallback(
    (slug: string): Subscription | undefined => {
      return subscriptions.find((subscription) => subscription.slug === slug);
    },
    [subscriptions]
  );

  const isSubscribedToTreatment = useCallback(
    (slug: string): boolean => {
      return subscriptions.some((subscription) => subscription.slug === slug);
    },
    [subscriptions]
  );

  const contextValue: SubscriptionContextType = {
    subscriptions,
    hasEverSubscribed,
    isLoading,
    error,
    refetchSubscriptions,
    findSubscriptionBySlug,
    isSubscribedToTreatment,
    portalLink,
    isPortalLinkLoading,
    portalLinkError,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};
